'use client';

import Link from 'next/link';
import { Icon } from '@petsmart-ui/sparky';

import { clsx, ADOPTION_COUNT, usePageLayout, fonts } from '@libs/common-next';
import { dataLayerPush, layoutEvents, getBaseUrlJoined } from '@libs/analytics';

import type { LivesSavedProps } from './types';

import './LivesSaved.scss';

export const trackingData = (data: object) => Object.assign(data, { ...layoutEvents.headerClick });

export default function LivesSaved({ className, variant, link, text, display = true }: LivesSavedProps) {
    const { adoptionCount } = usePageLayout();

    const classes = clsx(
        className,
        variant === 'footer' && ['lives-saved__text', 'lives-saved__text--strong'],
        variant === 'header' && ['lives-saved__text', 'lives-saved__text--normal']
    );

    const linkWrapperClasses = clsx(fonts.euclidCircularBRegular.className, 'link-wrapper');

    const shouldDisplay = display && adoptionCount;

    if (!shouldDisplay) {
        return null;
    }

    const livesSavedValue = adoptionCount?.toLocaleString();
    const livesSavedText = text || ADOPTION_COUNT.TEXT;

    switch (variant) {
        case 'footer':
            return (
                <div data-testid="lives-saved-footer" className="lives-saved--footer">
                    <Link
                        className={classes}
                        href={link?.webUrl || ADOPTION_COUNT.LINK}
                        prefetch={false} // https://petsmart.atlassian.net/browse/SB-325
                    >
                        <span>
                            <strong className={fonts.proximaNovaABlack.className}>{livesSavedValue}</strong>
                            &nbsp;
                            {livesSavedText}
                        </span>
                    </Link>
                </div>
            );
        case 'header':
            return (
                <div data-testid="lives-saved-header" className="lives-saved">
                    <Link
                        className={classes}
                        href={link?.webUrl || ADOPTION_COUNT.LINK}
                        onClick={() =>
                            dataLayerPush(
                                trackingData({
                                    selection: `${livesSavedValue} ${livesSavedText}`,
                                    link_url: getBaseUrlJoined(link?.webUrl || ADOPTION_COUNT.LINK),
                                })
                            )
                        }
                        prefetch={false} // https://petsmart.atlassian.net/browse/SB-325
                    >
                        <span className={linkWrapperClasses}>
                            <Icon name="charities-fill" size="sm" className="sparky-u-margin-right-xs" />
                            <span>{livesSavedValue}</span>
                            &nbsp;
                            {livesSavedText}
                        </span>
                    </Link>
                </div>
            );
        default:
            return null;
    }
}
